import React, { useCallback, useMemo, useRef } from "react";

import Actions from "./Actions";
import BaseImportModal from "@/Modals/WebDataManagement/Mapping/BaseImport/BaseImportModal";
import { CONFIRMATION_MODAL_TYPES } from "@/Modals/NewConfirmationModal/constants";
import DataTable from "@/components/NewTable";
import { useGet } from "@/apiHooks/HttpFunctions";
import { useModalConfirmation } from "@/context/ModalConfirmationContext";
import useUrlParams from "@/hooks/useUrlParams";
import { FILTER_TYPE, PER_PAGE } from "@/constant/constants";
import { statusFilters } from "@/constant/statusConstants";

export default function MappingBaseTable({
  label,
  apiURL,
  columns = [],
  filters = [],
  ModalComponent,
  importData,
  modalDataName = "",
  enableAdd = false,
  enableEdit = false,
  enableDelete = false,
  enableSortData = false,
  enableStatus = false,
}) {
  // Hooks
  const params = useUrlParams();
  const { openModalConfirmation } = useModalConfirmation();

  // Refs
  const modalRef = useRef(null);
  const modalImportRef = useRef(null);

  // Callbacks
  const handleModalAction = useCallback(
    (type, data) => {
      const title =
        modalDataName.split(".").reduce((acc, key) => acc?.[key], data) ||
        data?.name;
      openModalConfirmation({
        type,
        title: title,
        id: data?.id,
        apiURL,
      });
    },
    [openModalConfirmation, apiURL, modalDataName]
  );

  const handleOnClickAdd = useCallback(() => modalRef.current?.open(), []);
  const handleOnClickImport = useCallback(
    () => modalImportRef.current?.open(),
    []
  );
  const handleOnClickEdit = useCallback(
    (data) => modalRef.current?.open({ id: data.id }),
    []
  );

  // Data Fetching
  const { data, isFetching, error, refetch } = useGet({
    apiURL,
    params: {
      ...params,
      perPage: params.perPage || PER_PAGE,
    },
  });

  // Derived States
  const internalFilters = useMemo(() => {
    if (!enableStatus) return [];
    const statusFiltersMap = statusFilters();
    return [
      {
        id: "filter[status]",
        label: "Status",
        type: FILTER_TYPE.SELECT,
        options: Object.entries(statusFiltersMap).map(([value, label]) => ({
          label,
          value,
        })),
      },
    ];
  }, [enableStatus]);

  const filterData = useMemo(
    () => [...internalFilters, ...filters],
    [internalFilters, filters]
  );

  // Merged Columns
  const mergedColumns = useMemo(() => {
    const actionsColumn = {
      accessorKey: "actions",
      header: "Actions",
      enableResizing: false,
      enableSorting: false,
      cell: ({ row }) => (
        <Actions
          row={row}
          handleOnClickDelete={
            enableDelete &&
            ((data) => handleModalAction(CONFIRMATION_MODAL_TYPES.DELETE, data))
          }
          handleOnClickEdit={enableEdit && handleOnClickEdit}
        />
      ),
    };

    return [actionsColumn, ...columns];
  }, [columns, enableEdit, enableDelete, handleModalAction, handleOnClickEdit]);

  // Render
  if (error) {
    return (
      <div>
        <p>Error loading data...</p>
        <button onClick={refetch}>Retry</button>
      </div>
    );
  }

  return (
    <>
      <DataTable
        data={data}
        columns={mergedColumns}
        isLoading={isFetching}
        searchKey="filter[keyword]"
        exportData={{
          api: apiURL,
          label: `${label} List`,
        }}
        importData={{
          onClick: handleOnClickImport,
        }}
        addData={
          enableAdd && {
            onClick: handleOnClickAdd,
          }
        }
        sortData={
          enableSortData && {
            api: apiURL,
          }
        }
        filterData={filterData}
      />
      {ModalComponent && <ModalComponent ref={modalRef} />}
      {importData && (
        <BaseImportModal
          ref={modalImportRef}
          importData={importData}
          apiURL={apiURL}
        />
      )}
    </>
  );
}
